<script lang="ts">
	import Breadcrumbs from '$lib/components/ui/Breadcrumbs.svelte';
	import CoordsDecoration from '$lib/components/ui/CoordsDecoration.svelte';
	import type { BreadcrumbLink } from '$lib/features/_other/breadcrumbLink';
	import { type Coordinates } from '$lib/features/_other/coordinates.type';

	interface Star {
		top: string;
		left: string;
		size: number;
		delay: string;
		duration: string;
		peak: number;
	}

	const stars: Star[] = [
		{ top: '5.3%', left: '89.3%', size: 3.4, delay: '-2.89s', duration: '4.76s', peak: 0.69 },
		{ top: '6.2%', left: '82.1%', size: 2.8, delay: '-3.08s', duration: '3.95s', peak: 0.6 },
		{ top: '6.4%', left: '20.7%', size: 3.1, delay: '-1.18s', duration: '4.02s', peak: 0.65 },
		{ top: '7.1%', left: '50.1%', size: 2.8, delay: '-0.89s', duration: '3.95s', peak: 0.6 },
		{ top: '14.1%', left: '68.1%', size: 3.3, delay: '-3.29s', duration: '3.7s', peak: 0.58 },
		{ top: '25.0%', left: '29.2%', size: 2.3, delay: '-0.67s', duration: '3.21s', peak: 0.6 },
		{ top: '25.1%', left: '5.0%', size: 2.6, delay: '-1.48s', duration: '3.3s', peak: 0.64 },
		{ top: '25.6%', left: '82.1%', size: 2.8, delay: '-0.89s', duration: '3.95s', peak: 0.6 },
		{ top: '28.4%', left: '25.2%', size: 3.0, delay: '-0.81s', duration: '3.28s', peak: 0.81 },
		{ top: '35.7%', left: '12.6%', size: 3.6, delay: '-1.47s', duration: '3.92s', peak: 0.59 },
		{ top: '42.9%', left: '21.9%', size: 3.0, delay: '-1.26s', duration: '4.5s', peak: 0.64 },
		{ top: '45.7%', left: '91.7%', size: 3.4, delay: '-1.91s', duration: '3.28s', peak: 0.78 },
		{ top: '50.6%', left: '75.1%', size: 2.3, delay: '-2.36s', duration: '3.15s', peak: 0.77 },
		{ top: '62.5%', left: '36.3%', size: 2.7, delay: '-0.43s', duration: '3.14s', peak: 0.67 },
		{ top: '62.8%', left: '4.4%', size: 2.6, delay: '-1.45s', duration: '3.18s', peak: 0.88 },
		{ top: '63.6%', left: '59.3%', size: 2.4, delay: '-4.08s', duration: '4.5s', peak: 0.62 },
		{ top: '39.3%', left: '62.5%', size: 2.6, delay: '-4.16s', duration: '4.27s', peak: 0.6 },
		{ top: '72.2%', left: '52.7%', size: 3.3, delay: '-3.87s', duration: '3.98s', peak: 0.55 },
		{ top: '74.5%', left: '14.1%', size: 2.9, delay: '-1.45s', duration: '4.03s', peak: 0.67 },
		{ top: '76.3%', left: '70.6%', size: 3.0, delay: '-4.07s', duration: '5.13s', peak: 0.72 },
		{ top: '78.5%', left: '2.6%', size: 3.3, delay: '-2.1s', duration: '4.42s', peak: 0.7 },
		{ top: '80.3%', left: '60.1%', size: 3.4, delay: '-3.69s', duration: '4.1s', peak: 0.87 },
		{ top: '84.6%', left: '31.6%', size: 3.1, delay: '-0.67s', duration: '3.63s', peak: 0.96 },
		{ top: '85.8%', left: '91.0%', size: 2.3, delay: '-2.28s', duration: '3.86s', peak: 0.58 },
		{ top: '86.1%', left: '10.2%', size: 2.8, delay: '-1.07s', duration: '2.68s', peak: 0.65 },
		{ top: '88.0%', left: '83.8%', size: 2.6, delay: '-2.82s', duration: '4.26s', peak: 0.82 },
		{ top: '92.1%', left: '33.6%', size: 2.3, delay: '-0.47s', duration: '2.85s', peak: 0.93 },
		{ top: '95.0%', left: '62.2%', size: 3.0, delay: '-3.85s', duration: '4.38s', peak: 0.93 },
		{ top: '97.6%', left: '51.7%', size: 3.6, delay: '-4.76s', duration: '4.84s', peak: 0.56 }
	];

	const {
		title,
		description,
		extraInfos,
		breadcrumbs,
		decorations
	}: {
		title: string;
		description: string;
		extraInfos?: string[];
		breadcrumbs: BreadcrumbLink[];
		decorations?: {
			coordinates?: Coordinates[];
			hexColour?: string;
		};
	} = $props();
</script>

<header class="relative overflow-hidden bg-neutral px-6 py-20 text-neutral-content md:py-24">
	<div class="pointer-events-none absolute inset-0" aria-hidden="true" role="presentation">
		{@render RouteAnimation()}

		{#each stars as star}
			{@render Star(star)}
		{/each}
	</div>

	<div class="relative mx-auto flex max-w-3xl flex-col gap-5">
		<Breadcrumbs items={breadcrumbs} />

		<hgroup class="flex flex-col gap-5">
			<h1 class="text-4xl leading-[1.08] font-medium tracking-tight md:text-5xl">
				{title}
			</h1>
			<p class="max-w-xl text-lg leading-relaxed text-neutral-content/85">
				{description}
			</p>
		</hgroup>

		{@render ExtraInfos()}
	</div>

	<CoordsDecoration coordinates={[...(decorations?.coordinates ?? [])]} classes="text-neutral-content/70" />
</header>

{#snippet ExtraInfos()}
	{#if extraInfos && extraInfos.length > 0}
		<div class="flex flex-wrap items-center gap-1.5 text-xs text-neutral-content/70">
			{#each extraInfos as info, i}
				{#if i !== 0}
					·
				{/if}
				<i class="font-mono text-xs text-neutral-content/70">
					{info}
				</i>
			{/each}
		</div>
	{/if}
{/snippet}

{#snippet RouteAnimation()}
	<svg class="routes" viewBox="0 0 1000 500" preserveAspectRatio="none">
		<defs>
			<radialGradient id="traveller-glow">
				<stop offset="0%" stop-color="var(--color-yellow-200)" stop-opacity="0.9" />
				<stop offset="50%" stop-color="var(--color-yellow-200)" stop-opacity="0.3" />
				<stop offset="100%" stop-color="var(--color-yellow-200)" stop-opacity="0" />
			</radialGradient>
		</defs>

		<path class="route" d="M80,340 C250,260 330,120 560,180 S850,300 940,120" />

		<g class="traveller">
			<circle class="traveller-glow" r="10" fill="url(#traveller-glow)" />
			<circle class="traveller-core" r="2" fill="var(--color-yellow-200)" />
		</g>
	</svg>
{/snippet}

{#snippet Star(star: Star)}
	<span
		class="star absolute rounded-full"
		style="
			top: {star.top};
			left: {star.left};
			width: {star.size + 1}px;
			height: {star.size + 1}px;
			--peak: {star.peak};
			--duration: {star.duration};
			animation-delay: {star.delay};
		"
	></span>
{/snippet}

<style>
	.routes {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		opacity: 0.3;
	}

	.route {
		fill: none;
		stroke: rgb(255 255 255 / 0.12);
		stroke-width: 1.2;
		stroke-dasharray: 7 9;
	}

	.traveller {
		offset-path: path('M80,340 C250,260 330,120 560,180 S850,300 940,120');
		animation: travel 18s linear infinite;
		will-change: offset-distance;
	}

	@keyframes travel {
		0% {
			offset-distance: 0%;
		}
		100% {
			offset-distance: 100%;
		}
	}

	.star {
		background: radial-gradient(circle, var(--color-yellow-200) 0%, var(--color-yellow-200) 70%, transparent 90%);
		opacity: 0.1;
		will-change: opacity;
		animation: twinkle var(--duration, 2.5s) ease-in-out infinite;
	}

	@keyframes twinkle {
		0%,
		100% {
			opacity: 0.1;
		}
		50% {
			opacity: var(--peak, 0.9);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.star,
		.traveller-core,
		.traveller-glow {
			animation: none;
			opacity: 0.5;
		}
	}
</style>
